import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Session } from '@supabase/supabase-js';
import { auth } from '@/lib/supabase';

interface AuthState {
  // State
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  signUp: (
    email: string,
    password: string,
    userData?: { name?: string; surname?: string }
  ) => Promise<boolean>;
  signInWithPassword: (email: string, password: string) => Promise<boolean>;
  signInWithOAuth: (
    provider: 'google' | 'github' | 'discord' | 'facebook' | 'apple'
  ) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

export const useSupabaseAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      session: null,
      isLoading: false,
      isInitialized: false,
      error: null,

      // Initialize auth state and set up listener
      initialize: async () => {
        if (get().isInitialized) return;

        set({ isLoading: true, error: null });

        try {
          // Get initial session
          const {
            data: { session },
            error,
          } = await auth.getSession();

          if (error) throw error;

          set({
            session,
            user: session?.user ?? null,
            isInitialized: true,
            isLoading: false,
          });

          // Set up auth state change listener
          auth.onAuthStateChange((event, session) => {
            console.log('Auth state changed:', event, session?.user?.email);

            set({
              session,
              user: session?.user ?? null,
              isLoading: false,
              error: null,
            });

            // Handle specific auth events
            if (event === 'SIGNED_OUT') {
              // Clear any app-specific data here
              console.log('User signed out');
            } else if (event === 'SIGNED_IN') {
              console.log('User signed in:', session?.user?.email);
            } else if (event === 'TOKEN_REFRESHED') {
              console.log('Token refreshed');
            }
          });
        } catch (error) {
          console.error('Auth initialization error:', error);
          set({
            error: error instanceof Error ? error.message : 'Authentication initialization failed',
            isLoading: false,
            isInitialized: true,
          });
        }
      },

      // Sign up with email/password
      signUp: async (
        email: string,
        password: string,
        userData?: { name?: string; surname?: string }
      ) => {
        set({ isLoading: true, error: null });

        try {
          const { error } = await auth.signUp(email, password, {
            data: {
              name: userData?.name || '',
              surname: userData?.surname || '',
            },
          });

          if (error) throw error;

          // Supabase will automatically update auth state via listener
          set({ isLoading: false });

          return true;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Sign up failed';
          set({ error: errorMessage, isLoading: false });
          return false;
        }
      },

      // Sign in with email/password
      signInWithPassword: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const { error } = await auth.signInWithPassword(email, password);

          if (error) throw error;

          // Supabase will automatically update auth state via listener
          set({ isLoading: false });

          return true;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
          set({ error: errorMessage, isLoading: false });
          return false;
        }
      },

      // Sign in with OAuth provider
      signInWithOAuth: async (provider: 'google' | 'github' | 'discord' | 'facebook' | 'apple') => {
        set({ isLoading: true, error: null });

        try {
          const { error } = await auth.signInWithOAuth(provider);

          if (error) throw error;

          // OAuth will redirect, so we don't need to handle the response here
          // The auth state will be updated after redirect via the callback page
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'OAuth sign in failed';
          set({ error: errorMessage, isLoading: false });
        }
      },

      // Sign out
      signOut: async () => {
        set({ isLoading: true, error: null });

        try {
          const { error } = await auth.signOut();

          if (error) throw error;

          // Supabase will automatically update auth state via listener
          set({ isLoading: false });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Sign out failed';
          set({ error: errorMessage, isLoading: false });
        }
      },

      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: 'supabase-auth-storage',
      // Only persist user and session, not loading states
      partialize: (state) => ({
        user: state.user,
        session: state.session,
      }),
    }
  )
);

// Computed values
export const useAuth = () => {
  const store = useSupabaseAuthStore();
  return {
    ...store,
    isAuthenticated: !!store.user && !!store.session,
    isAnonymous: !store.user,
  };
};
